import { createRoot } from 'react-dom/client';
import type { QueryClient } from '@tanstack/react-query';
import type { UserData } from '../providers/user-provider';
import {
  type ErrorObject,
  getErrorWithType,
  isScaError,
  SCA_ERROR_TYPE,
  type ScaErrorType,
} from '../utils/sca-errors';
import { extractScaErrorData } from '../utils/response-error-handler';
import { createPortalContainer } from '../utils/dom';
import { Providers } from '../providers';
import { faqService, type FaqService } from '../utils/faq-composer';
import { MfaErrorUI } from './mfa-error-ui';
import { ScaAuthUI } from './sca-auth-ui';
import { ScaEnforcementErrorUI } from './sca-enforcement-error-ui';
import { EmailVerificationUI } from './email-verification-ui';

export interface ScaParams {
  header: 'X-Qonto-MFA' | 'X-Qonto-Sca-Session-Token';
  token: string;
}

export type OnSensitiveAction = (params?: ScaParams) => Promise<void>;

export type SensitiveActionResult<T = unknown> =
  | { status: 'completed'; data: T }
  | { status: 'cancelled' };

export interface RenderScaParams {
  root: ReturnType<typeof createRoot>;
  cancel: () => void;
  onSensitiveAction: OnSensitiveAction;
  errorType: ScaErrorType;
  errorDetails: ErrorObject;
  user: UserData;
  queryClient?: QueryClient;
}

export interface ErrorComponentProps {
  onClose: () => void;
  onSensitiveAction: OnSensitiveAction;
  errorDetails: ErrorObject;
}

export function renderSca({
  root,
  cancel,
  onSensitiveAction,
  errorType,
  errorDetails,
  user,
  queryClient,
}: RenderScaParams): void {
  let SCAErrorComponent;

  switch (errorType) {
    case SCA_ERROR_TYPE.MFA:
      SCAErrorComponent = MfaErrorUI;
      break;
    case SCA_ERROR_TYPE.SCA:
      SCAErrorComponent = ScaAuthUI;
      break;
    case SCA_ERROR_TYPE.SCA_ENFORCEMENT:
      SCAErrorComponent = ScaEnforcementErrorUI;
      break;
    case SCA_ERROR_TYPE.EMAIL_VERIFICATION:
      SCAErrorComponent = EmailVerificationUI;
      break;
    default:
      // NOTE: if is not a SCA error, we just cleanup and return
      cancel();
      return;
  }

  root.render(
    <Providers queryClient={queryClient} user={user}>
      <SCAErrorComponent
        errorDetails={errorDetails}
        onClose={cancel}
        onSensitiveAction={onSensitiveAction}
      />
    </Providers>
  );
}

export async function sensitiveAction<T = unknown>({
  action,
  onScaSuccess,
  hostFaqService,
  user,
  queryClient,
}: {
  action: () => Promise<T>;
  onScaSuccess?: (params: ScaParams) => Promise<void> | void;
  hostFaqService: FaqService;
  user: UserData;
  queryClient?: QueryClient;
}): Promise<SensitiveActionResult<T>> {
  try {
    const data = await action();
    return { status: 'completed', data };
  } catch (error) {
    if (!isScaError(error)) {
      // oxlint-disable-next-line typescript/no-base-to-string
      throw error instanceof Error ? error : new Error(String(error));
    }

    const { errorData } = extractScaErrorData(error);
    const { type: errorType, error: errorDetails } = getErrorWithType(errorData);

    // oxlint-disable-next-line typescript/consistent-type-assertions
    if (!errorType || !errorDetails) throw error as Error;

    // NOTE: we need to use the host faq service to get the localized article, this is a temporary solution while we migrate zendesk to vanilla javascript
    faqService.getLocalizedArticle = articleSlug => hostFaqService.getLocalizedArticle(articleSlug);
    faqService.getLocalizedHelpCenter = hostFaqService.getLocalizedHelpCenter;

    return new Promise<SensitiveActionResult<T>>((resolve, reject) => {
      const portalContainer = createPortalContainer('sca-portal-container');
      const root = createRoot(portalContainer);

      const cleanup = (): void => {
        portalContainer.remove();
        root.unmount();
      };

      const cancel = (): void => {
        cleanup();
        resolve({ status: 'cancelled' });
      };

      const onSensitiveAction: (params?: ScaParams) => Promise<void> = async params => {
        cleanup();
        // NOTE: in some cases we do not call the onScaSuccess callback because Backend will handle the job for us.
        if (params) await onScaSuccess?.(params);
        try {
          const result = await sensitiveAction({
            action,
            onScaSuccess,
            hostFaqService,
            user,
            queryClient,
          });
          resolve(result);
          // oxlint-disable-next-line no-shadow
        } catch (error) {
          // oxlint-disable-next-line typescript/consistent-type-assertions
          if (!isScaError(error)) reject(error as Error);
        }
      };

      window.addEventListener('popstate', cleanup);
      renderSca({
        root,
        cancel,
        onSensitiveAction,
        errorType,
        errorDetails,
        user,
        queryClient,
      });
    });
  }
}
